import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, ArrowRight } from 'lucide-react';
import { useLang, useT } from '../i18n/LangContext';
import { useContent } from '../data/useContent';
import { PHONE, PHONE_HREF } from '../data/content';

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className="flex items-center rounded-full border border-white/20 p-0.5 text-[11px] font-extrabold tracking-wider">
      {['en', 'fr'].map((l) => (
        <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)} aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors ${lang === l ? 'bg-gold text-ink' : 'text-white/60 hover:text-white'}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function OfferBanner() {
  const t = useT();
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div data-testid="offer-banner" className="relative z-[60] bg-gold px-4 py-2.5 text-center text-ink">
      <p className="text-xs font-bold uppercase tracking-widest sm:text-sm">
        {t['banner.text']}{' '}
        <Link data-testid="offer-banner-link" to="/quote" className="underline underline-offset-4 hover:opacity-70">{t['banner.claim']}</Link>
      </p>
      <button data-testid="offer-banner-dismiss" onClick={() => setDismissed(true)} aria-label={t['banner.dismiss']}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:opacity-60">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function Header() {
  const t = useT();
  const { SERVICES } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const NAV_LINKS = [
    { to: '/gallery', label: t['nav.gallery'], key: 'gallery' },
    { to: '/reviews', label: t['nav.reviews'], key: 'reviews' },
    { to: '/about', label: t['nav.about'], key: 'about' },
    { to: '/blog', label: t['nav.blog'], key: 'blog' },
    { to: '/contact', label: t['nav.contact'], key: 'contact' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <OfferBanner />
      <header data-testid="site-header" className={`sticky top-0 z-50 transition-[background-color,border-color] duration-300 ${scrolled || open ? 'border-b border-white/10 bg-black/60 backdrop-blur-xl' : 'bg-transparent'}`}>
        <div className="container-site flex h-18 items-center justify-between py-4">
          <Link data-testid="logo-link" to="/" className="flex flex-col leading-none">
            <span className="font-serif text-2xl font-bold tracking-wide text-white">REVERENCE</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-gold">Services</span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Main navigation">
            <div className="group relative">
              <button data-testid="nav-services-dropdown" className="flex items-center gap-1 text-sm font-semibold text-white/85 transition-colors hover:text-gold">
                {t['nav.services']} <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <div className="invisible absolute left-0 top-full w-72 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                <div className="rounded-lg border border-white/10 bg-coal/95 p-2 shadow-2xl backdrop-blur-xl">
                  {SERVICES.map((s) => (
                    <Link key={s.slug} data-testid={`nav-service-${s.slug}`} to={`/${s.slug}`}
                      className="block rounded-md px-4 py-2.5 text-sm text-white/80 transition-colors hover:bg-white/5 hover:text-gold">
                      {s.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} data-testid={`nav-${l.key}`} to={l.to}
                className={({ isActive }) => `text-sm font-semibold transition-colors hover:text-gold ${isActive ? 'text-gold' : 'text-white/85'}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-5 lg:flex">
            <LangToggle />
            <a data-testid="header-phone" href={PHONE_HREF} className="flex items-center gap-2 text-sm font-bold text-gold transition-colors hover:text-gold-light">
              <Phone className="h-4 w-4" /> {PHONE}
            </a>
            <Link data-testid="header-quote-cta" to="/quote" className="gold-btn !px-5 !py-2.5">
              {t['nav.freeQuote']} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="flex items-center gap-3 lg:hidden">
            <LangToggle />
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label="Toggle menu" className="p-2 text-white">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="border-t border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden">
            <nav className="container-site flex flex-col gap-1 py-4" aria-label="Mobile navigation">
              <p className="px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.25em] text-gold">{t['nav.services']}</p>
              {SERVICES.map((s) => (
                <Link key={s.slug} data-testid={`mobile-service-${s.slug}`} to={`/${s.slug}`} className="rounded-md px-2 py-2 text-sm text-white/80 hover:text-gold">{s.name}</Link>
              ))}
              <p className="px-2 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.25em] text-gold">{t['nav.company']}</p>
              {NAV_LINKS.map((l) => (
                <Link key={l.to} data-testid={`mobile-nav-${l.key}`} to={l.to} className="rounded-md px-2 py-2 text-sm text-white/80 hover:text-gold">{l.label}</Link>
              ))}
              <a data-testid="mobile-header-phone" href={PHONE_HREF} className="mt-3 flex items-center gap-2 px-2 text-sm font-bold text-gold">
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
